import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import '../../Styles/ManagementPages.css';
import '../../Styles/SeatManagement.css';
import {
  getAllSeatCategories,
  getSeatCategoryById,
  createSeatCategory,
  updateSeatCategory,
  deleteSeatCategory,
  getAllSeatLayouts,
  getSeatLayoutById,
  createSeatLayout,
  updateSeatLayout,
  deleteSeatLayout,
  previewSeatLayout,
  getAvailableConfigurations,
} from '../../Services/Operations/ProductAPI';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Name</th>
          <th>Configuration</th>
          <th>Layouts</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        <div className="skeleton-detail-header"></div>
        <div className="skeleton-description"></div>
        <div className="detail-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skeleton-detail-item">
              <div className="skeleton-label"></div>
              <div className="skeleton-value"></div>
            </div>
          ))}
        </div>
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const SkeletonFormModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="form-group">
            <div className="skeleton-label"></div>
            <div className="skeleton-input"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const SeatManagement = () => {
  const { token } = useSelector(state => state.user);
  const [activeTab, setActiveTab] = useState('categories');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);

  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    configuration: ''
  });
  const [categoryError, setCategoryError] = useState(null);

  const [layouts, setLayouts] = useState([]);
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [editingLayout, setEditingLayout] = useState(null);
  const [layoutForm, setLayoutForm] = useState({
    name: '',
    description: '',
    category: '',
    seatType: 'seat',
    rows: 1
  });
  const [layoutError, setLayoutError] = useState(null);
  const [layoutPreview, setLayoutPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [availableConfigurations, setAvailableConfigurations] = useState([]);

  const [viewingCategory, setViewingCategory] = useState(null);
  const [viewingLayout, setViewingLayout] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(10);

  const safeText = (value, fallback = 'N/A') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    if (typeof value === 'object') {
      if (value.name) return value.name;
      return JSON.stringify(value);
    }
    return String(value);
  };

  useEffect(() => {
    if (activeTab === 'categories') {
      fetchCategories();
    } else {
      fetchLayouts();
    }
  }, [currentPage, searchTerm, activeTab]);

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      const response = await getAvailableConfigurations(token);
      if (response.success) {
        setAvailableConfigurations(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      setSearching(true);
      setLoading(true);
      const response = await getAllSeatCategories(token, currentPage, pageSize, searchTerm);

      if (response && response.data) {
        const transformedCategories = response.data.map((category, index) => ({
          id: String(category._id || ''),
          serialNo: (currentPage - 1) * pageSize + index + 1,
          name: String(category.name || ''),
          description: String(category.description || ''),
          configuration: String(category.configuration || ''),
          leftSeats: Number(category.leftSeats || 0),
          rightSeats: Number(category.rightSeats || 0),
          totalSeatsPerRow: Number(category.totalSeatsPerRow || 0),
          layoutCount: Number(category.layoutCount || 0),
          isActive: Boolean(category.isActive || false),
          rawData: category
        }));

        setCategories(transformedCategories);
        setTotalPages(response.totalPages || 1);
        setTotalItems(response.total || transformedCategories.length);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const fetchLayouts = async () => {
    try {
      setSearching(true);
      setLoading(true);
      const response = await getAllSeatLayouts(token, currentPage, pageSize, searchTerm);

      if (response && response.data) {
        const transformedLayouts = response.data.map((layout, index) => {
          let categoryName = 'Unknown';
          let categoryId = '';

          if (layout.category) {
            if (typeof layout.category === 'object') {
              categoryName = String(layout.category.name || 'Unknown');
              categoryId = layout.category._id || '';
            } else {
              categoryName = String(layout.category);
              categoryId = String(layout.category || '');
            }
          }

          return {
            id: String(layout._id || ''),
            serialNo: (currentPage - 1) * pageSize + index + 1,
            name: String(layout.name || ''),
            description: String(layout.description || ''),
            category: categoryName,
            categoryId: categoryId,
            configuration: String(layout.configuration || ''),
            seatType: String(layout.seatType || 'seat'),
            rows: Number(layout.rows || 0),
            totalSeats: Number(layout.totalSeats || 0),
            isActive: Boolean(layout.isActive || false),
            rawData: layout
          };
        });

        setLayouts(transformedLayouts);
        setTotalPages(response.totalPages || 1);
        setTotalItems(response.total || transformedLayouts.length);
      }
    } catch (error) {
      console.error('Error fetching layouts:', error);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleViewCategory = async (category) => {
    try {
      setViewLoading(true);
      const response = await getSeatCategoryById(category.id, token);

      if (response.success) {
        setViewingCategory(response.data);
      } else {
        setViewingCategory(category.rawData || category);
      }
    } catch (error) {
      console.error('Error viewing category:', error);
      setViewingCategory(category.rawData || category);
    } finally {
      setViewLoading(false);
    }
  };

  const handleViewLayout = async (layout) => {
    try {
      setViewLoading(true);
      const response = await getSeatLayoutById(layout.id, token);

      if (response.success) {
        setViewingLayout(response.data);
      } else {
        setViewingLayout(layout.rawData || layout);
      }
    } catch (error) {
      console.error('Error viewing layout:', error);
      setViewingLayout(layout.rawData || layout);
    } finally {
      setViewLoading(false);
    }
  };

  const handleCreateCategory = (e) => {
    if (e) e.stopPropagation();
    setEditingCategory(null);
    setCategoryForm({ name: '', description: '', configuration: '' });
    setCategoryError(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category, e) => {
    if (e) e.stopPropagation();
    setEditingCategory(category.id);
    setCategoryForm({
      name: String(category.name || ''),
      description: String(category.description || ''),
      configuration: String(category.configuration || '')
    });
    setCategoryError(null);
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    try {
      setCategoryError(null);

      if (!categoryForm.name || !categoryForm.description || !categoryForm.configuration) {
        setCategoryError('All fields are required');
        return;
      }

      if (editingCategory) {
        const response = await updateSeatCategory(editingCategory, categoryForm, token);
        if (response.success) {
          await fetchCategories();
          setShowCategoryModal(false);
          alert('Category updated successfully!');
        } else {
          setCategoryError(response.message || 'Failed to update category');
        }
      } else {
        const response = await createSeatCategory(categoryForm, token);
        if (response.success) {
          await fetchCategories();
          setShowCategoryModal(false);
          alert('Category created successfully!');
        } else {
          setCategoryError(response.message || 'Failed to create category');
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      setCategoryError(error.message || 'An error occurred');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        const response = await deleteSeatCategory(id, token);
        if (response.success) {
          await fetchCategories();
          alert('Category deleted successfully!');
        } else {
          alert(response.message || 'Failed to delete category');
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Error deleting category');
      }
    }
  };

  const handleCreateLayout = (e) => {
    if (e) e.stopPropagation();
    setEditingLayout(null);
    setLayoutForm({
      name: '',
      description: '',
      category: '',
      seatType: 'seat',
      rows: 1
    });
    setLayoutError(null);
    setLayoutPreview(null);
    setShowLayoutModal(true);
  };

  const handleEditLayout = (layout, e) => {
    if (e) e.stopPropagation();
    setEditingLayout(layout.id);
    setLayoutForm({
      name: String(layout.name || ''),
      description: String(layout.description || ''),
      category: String(layout.categoryId || ''),
      seatType: String(layout.seatType || 'seat'),
      rows: Number(layout.rows || 1)
    });
    setLayoutError(null);
    setLayoutPreview(null);
    setShowLayoutModal(true);
  };

  const handleGeneratePreview = async () => {
    try {
      if (!layoutForm.category || !layoutForm.seatType || layoutForm.rows < 1) {
        setLayoutError('Please fill all required fields');
        return;
      }

      setLoadingPreview(true);
      const previewData = {
        category: layoutForm.category,
        seatType: layoutForm.seatType,
        rows: parseInt(layoutForm.rows),
        basePrice: 1200
      };

      const response = await previewSeatLayout(previewData, token);
      if (response.success) {
        setLayoutPreview(response.data);
      } else {
        setLayoutError(response.message || 'Failed to generate preview');
      }
    } catch (error) {
      setLayoutError(error.message || 'Error generating preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSaveLayout = async () => {
    try {
      setLayoutError(null);

      if (!layoutPreview) {
        setLayoutError('Please generate a preview first');
        return;
      }

      if (!layoutForm.name || !layoutForm.description) {
        setLayoutError('Name and description are required');
        return;
      }

      const layoutData = {
        name: layoutForm.name,
        description: layoutForm.description,
        category: layoutForm.category,
        seatType: layoutForm.seatType,
        rows: parseInt(layoutForm.rows)
      };

      if (editingLayout) {
        const response = await updateSeatLayout(editingLayout, layoutData, token);
        if (response.success) {
          await fetchLayouts();
          setShowLayoutModal(false);
          alert('Layout updated successfully!');
        } else {
          setLayoutError(response.message || 'Failed to update layout');
        }
      } else {
        const response = await createSeatLayout(layoutData, token);
        if (response.success) {
          await fetchLayouts();
          setShowLayoutModal(false);
          alert('Layout created successfully!');
        } else {
          setLayoutError(response.message || 'Failed to create layout');
        }
      }
    } catch (error) {
      console.error('Error saving layout:', error);
      setLayoutError(error.message || 'An error occurred');
    }
  };

  const handleDeleteLayout = async (id) => {
    if (window.confirm('Are you sure you want to delete this layout?')) {
      try {
        const response = await deleteSeatLayout(id, token);
        if (response.success) {
          await fetchLayouts();
          alert('Layout deleted successfully!');
        } else {
          alert(response.message || 'Failed to delete layout');
        }
      } catch (error) {
        console.error('Error deleting layout:', error);
        alert('Error deleting layout');
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    if (activeTab === 'categories') {
      fetchCategories();
    } else {
      fetchLayouts();
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleExportCSV = () => {
    const data = activeTab === 'categories' ? categories : layouts;
    if (data.length === 0) {
      alert('No data to export');
      return;
    }

    let headers, csvData;

    if (activeTab === 'categories') {
      headers = ['No.', 'Name', 'Configuration', 'Left Seats', 'Right Seats', 'Total/Row', 'Layouts', 'Status'];
      csvData = data.map(item => [
        item.serialNo,
        item.name,
        item.configuration,
        item.leftSeats,
        item.rightSeats,
        item.totalSeatsPerRow,
        item.layoutCount,
        item.isActive ? 'Active' : 'Inactive'
      ]);
    } else {
      headers = ['No.', 'Name', 'Category', 'Seat Type', 'Configuration', 'Rows', 'Total Seats', 'Status'];
      csvData = data.map(item => [
        item.serialNo,
        item.name,
        item.category,
        item.seatType,
        item.configuration,
        item.rows,
        item.totalSeats,
        item.isActive ? 'Active' : 'Inactive'
      ]);
    }

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeTab}-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('CSV exported successfully!');
  };

  const renderSeatPreview = () => {
    if (!layoutPreview || !layoutPreview.seatPreview) return null;

    const { seatPreview, totalRows, seatsPerRow, configuration, totalSeats, backSeats } = layoutPreview;

    const seatsByRow = {};
    seatPreview.forEach(seat => {
      if (!seatsByRow[seat.row]) {
        seatsByRow[seat.row] = [];
      }
      seatsByRow[seat.row].push(seat);
    });

    const sortedRows = Object.keys(seatsByRow).sort((a, b) => Number(a) - Number(b));
    const [leftCount, rightCount] = configuration.split('*').map(Number);

    return (
      <div className="preview-container">
        <h4>Seat Layout Preview</h4>
        <div className="seat-preview-grid">
          {sortedRows.map(rowNum => {
            const isBackRow = rowNum > totalRows - (backSeats / seatsPerRow);
            const rowSeats = seatsByRow[rowNum].sort((a, b) => a.column - b.column);
            const leftSeats = rowSeats.filter(seat => seat.position === 'left');
            const rightSeats = rowSeats.filter(seat => seat.position === 'right' || seat.position === 'middle');

            return (
              <div key={rowNum} className={`preview-row ${isBackRow ? 'back-row' : ''}`}>
                <div className="row-label">{isBackRow ? 'Back Row' : `Row ${rowNum}`}</div>
                <div className="row-seats">
                  <div className="seat-group left-group">
                    {leftSeats.map(seat => (
                      <div key={seat.seatNumber} className="preview-seat left" title={`${seat.seatNumber} - Left`}>
                        <span className="seat-number">{seat.seatNumber}</span>
                      </div>
                    ))}
                  </div>
                  <div className="aisle-space">
                    <span className="aisle-label">Aisle</span>
                  </div>
                  <div className="seat-group right-group">
                    {rightSeats.map(seat => (
                      <div key={seat.seatNumber} className="preview-seat right" title={`${seat.seatNumber} - Right`}>
                        <span className="seat-number">{seat.seatNumber}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="row-summary">
                  <span className="left-count">{leftSeats.length}L</span>
                  <span className="right-count">{rightSeats.length}R</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="preview-legend">
          <div className="legend-item">
            <div className="legend-color left-seat"></div>
            <span>Left Seat ({leftCount} per row)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color right-seat"></div>
            <span>Right Seat ({rightCount} per row)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color back-seat"></div>
            <span>Back Row Seats</span>
          </div>
          <div className="legend-item">
            <div className="legend-color aisle"></div>
            <span>Aisle (Center)</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading && (activeTab === 'categories' ? categories.length === 0 : layouts.length === 0)) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="tabs-container">
          <div className="tabs">
            <div className="skeleton-tab"></div>
            <div className="skeleton-tab"></div>
          </div>
          <div className="skeleton-create-btn"></div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Seat Management</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "300px" }}
            />
            {searching && (
              <span style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
              }}>
                searching...
              </span>
            )}
          </div>
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
        </div>
      </div>


      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Seat Categories
          </button>
          <button
            className={`tab-btn ${activeTab === 'layouts' ? 'active' : ''}`}
            onClick={() => setActiveTab('layouts')}
          >
            Seat Layouts
          </button>
        </div>

        <div className="tab-actions">
          <button
            className="create-btn"
            onClick={activeTab === 'categories' ? handleCreateCategory : handleCreateLayout}
          >
            + Create New {activeTab === 'categories' ? 'Category' : 'Layout'}
          </button>
        </div>
      </div>


      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total {activeTab === 'categories' ? 'Categories' : 'Layouts'}</h3>
          <p>{totalItems}</p>
          <span>All {activeTab} in system</span>
        </div>

        {activeTab === 'categories' ? (
          <>
            <div className="stat-card">
              <h3>Active Categories</h3>
              <p>{categories.filter(c => c.isActive).length}</p>
              <span>Currently active</span>
            </div>
            <div className="stat-card">
              <h3>Total Layouts</h3>
              <p>{categories.reduce((sum, cat) => sum + (cat.layoutCount || 0), 0)}</p>
              <span>Across all categories</span>
            </div>
            <div className="stat-card">
              <h3>Search Results</h3>
              <p>{categories.length}</p>
              <span>{activeTab} found</span>
            </div>
          </>
        ) : (
          <>
            <div className="stat-card">
              <h3>Active Layouts</h3>
              <p>{layouts.filter(l => l.isActive).length}</p>
              <span>Currently active</span>
            </div>
            <div className="stat-card">
              <h3>Seat Types</h3>
              <p>{[...new Set(layouts.map(l => l.seatType))].length}</p>
              <span>Different seat types</span>
            </div>
            <div className="stat-card">
              <h3>Total Seats</h3>
              <p>{layouts.reduce((sum, layout) => sum + (layout.totalSeats || 0), 0)}</p>
              <span>Across all layouts</span>
            </div>
          </>
        )}
      </div>


      <div className="table-container">
        {activeTab === 'categories' ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Name</th>
                <th>Configuration</th>
                <th>Layouts</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length > 0 ? (
                categories.map(category => (
                  <tr key={category.id}>
                    <td>{category.serialNo}</td>
                    <td>
                      <div className="category-name">
                        <strong>{category.name}</strong>
                        <small>{category.description}</small>
                      </div>
                    </td>
                    <td>
                      <span className="config-badge">{category.configuration}</span>
                    </td>
                    <td>{category.layoutCount}</td>
                    <td>
                      <span className={`status-badge ${category.isActive ? 'active' : 'inactive'}`}>
                        {category.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn view" onClick={() => handleViewCategory(category)} disabled={viewLoading}>
                          {viewLoading && viewingCategory?.id === category.id ? 'Loading...' : 'View'}
                        </button>
                        <button className="action-btn edit" onClick={(e) => handleEditCategory(category, e)}>Edit</button>
                        <button className="action-btn delete" onClick={() => handleDeleteCategory(category.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="no-data"><p>No categories found {searchTerm ? 'matching your search' : ''}.</p>{searchTerm && (<button className="clear-filters-link" onClick={() => { setSearchTerm(''); setCurrentPage(1); }}>Clear search</button>)}</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Name</th>
                <th>Category</th>
                <th>Seat Type</th>
                <th>Total Seats</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {layouts.length > 0 ? (
                layouts.map(layout => (
                  <tr key={layout.id}>
                    <td>{layout.serialNo}</td>
                    <td>
                      <div className="layout-name">
                        <strong>{layout.name}</strong>
                        <small>{layout.description}</small>
                      </div>
                    </td>
                    <td>{layout.category}</td>
                    <td>
                      <span className={`seat-type-badge ${layout.seatType}`}>{layout.seatType}</span>
                    </td>
                    <td>{layout.totalSeats}</td>
                    <td>
                      <span className={`status-badge ${layout.isActive ? 'active' : 'inactive'}`}>
                        {layout.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn view" onClick={() => handleViewLayout(layout)} disabled={viewLoading}>
                          {viewLoading && viewingLayout?.id === layout.id ? 'Loading...' : 'View'}
                        </button>
                        <button className="action-btn edit" onClick={(e) => handleEditLayout(layout, e)}>Edit</button>
                        <button className="action-btn delete" onClick={() => handleDeleteLayout(layout.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" className="no-data"><p>No layouts found {searchTerm ? 'matching your search' : ''}.</p>{searchTerm && (<button className="clear-filters-link" onClick={() => { setSearchTerm(''); setCurrentPage(1); }}>Clear search</button>)}</td></tr>
              )}
            </tbody>
          </table>
        )}

        {(activeTab === 'categories' ? categories.length : layouts.length) === 0 && !loading && (
          <div className="no-data">
            <p>No {activeTab} found matching your search.</p>
          </div>
        )}
      </div>


      <div className="pagination-container">
        <div className="pagination-info">
          Showing {(activeTab === 'categories' ? categories.length : layouts.length)} of {totalItems} {activeTab}
          (Page {currentPage} of {totalPages})
          {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
        </div>
        <div className="pagination-controls">
          <button className="pagination-btn" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber;
              if (totalPages <= 5) pageNumber = i + 1;
              else if (currentPage <= 3) pageNumber = i + 1;
              else if (currentPage >= totalPages - 2) pageNumber = totalPages - 4 + i;
              else pageNumber = currentPage - 2 + i;
              return (<button key={pageNumber} className={`page-number ${pageNumber === currentPage ? 'active' : ''}`} onClick={() => handlePageClick(pageNumber)}>{pageNumber}</button>);
            })}
          </div>
          <button className="pagination-btn" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
        </div>
      </div>


      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCategory ? 'Edit Category' : 'Create New Category'}</h3>
              <button className="modal-close-btn" onClick={() => setShowCategoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {categoryError && <div className="error-message">{categoryError}</div>}
              <div className="form-group">
                <label>Category Name *</label>
                <input type="text" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} placeholder="e.g., Standard 2x2 Layout" className="form-input" />
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} placeholder="Describe this seat configuration" className="form-input" rows="3" />
              </div>
              <div className="form-group">
                <label>Configuration *</label>
                <input type="text" value={categoryForm.configuration} onChange={(e) => setCategoryForm({ ...categoryForm, configuration: e.target.value })} placeholder="e.g., 2*2, 3*2, 4*3" className="form-input" />
                <small className="form-hint">Format: leftSeats*rightSeats</small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowCategoryModal(false)}>Cancel</button>
              <button className="modal-btn save" onClick={handleSaveCategory}>{editingCategory ? 'Update' : 'Create'} Category</button>
            </div>
          </div>
        </div>
      )}


      {showLayoutModal && (
        <div className="modal-overlay" onClick={() => setShowLayoutModal(false)}>
          <div className="modal-content1 modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingLayout ? 'Edit Layout' : 'Create New Layout'}</h3>
              <button className="modal-close-btn" onClick={() => setShowLayoutModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {layoutError && <div className="error-message">{layoutError}</div>}
              <div className="form-row">
                <div className="form-group">
                  <label>Layout Name *</label>
                  <input type="text" value={layoutForm.name} onChange={(e) => setLayoutForm({ ...layoutForm, name: e.target.value })} placeholder="e.g., Economy 2x2 Bus" className="form-input" />
                </div>
                <div className="form-group">
                  <label>Description *</label>
                  <input type="text" value={layoutForm.description} onChange={(e) => setLayoutForm({ ...layoutForm, description: e.target.value })} placeholder="e.g., Economy class with regular seats" className="form-input" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select value={layoutForm.category} onChange={(e) => setLayoutForm({ ...layoutForm, category: e.target.value })} className="form-input">
                    <option value="">Select Category</option>
                    {categories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name} ({cat.configuration})</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Seat Type *</label>
                  <select value={layoutForm.seatType} onChange={(e) => setLayoutForm({ ...layoutForm, seatType: e.target.value })} className="form-input">
                    <option value="seat">Regular Seat</option>
                    <option value="sleeper">Sleeper</option>
                    <option value="seater+sleeper">Seat + Sleeper</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Number of Rows *</label>
                  <input type="number" value={layoutForm.rows} onChange={(e) => setLayoutForm({ ...layoutForm, rows: parseInt(e.target.value) || 1 })} min="1" className="form-input" />
                </div>
              </div>
              <div className="form-actions">
                <button className="preview-btn" onClick={handleGeneratePreview} disabled={loadingPreview || !layoutForm.category || !layoutForm.seatType}>
                  {loadingPreview ? 'Generating...' : 'Generate Preview'}
                </button>
              </div>
              {layoutPreview && renderSeatPreview()}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowLayoutModal(false)}>Cancel</button>
              <button className="modal-btn save" onClick={handleSaveLayout} disabled={!layoutPreview}>
                {editingLayout ? 'Update' : 'Create'} Layout
              </button>
            </div>
          </div>
        </div>
      )}


      {viewingCategory && (
        <div className="modal-overlay" onClick={() => setViewingCategory(null)}>
          <div className="modal-content1 modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Category Details</h3>
              <button className="modal-close-btn" onClick={() => setViewingCategory(null)}>×</button>
            </div>
            <div className="modal-body">
              {viewLoading ? (
                <div className="loading-container"><p>Loading category details...</p></div>
              ) : (
                <div className="category-details">
                  <div className="detail-header">
                    <h4>{safeText(viewingCategory.name)}</h4>
                    <span className={`status-badge ${viewingCategory.isActive ? 'active' : 'inactive'}`}>
                      {viewingCategory.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="description">{safeText(viewingCategory.description)}</p>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Configuration:</label><span className="config-value">{safeText(viewingCategory.configuration)}</span></div>
                    <div className="detail-item"><label>Left Seats:</label><span>{viewingCategory.leftSeats || 0}</span></div>
                    <div className="detail-item"><label>Right Seats:</label><span>{viewingCategory.rightSeats || 0}</span></div>
                    <div className="detail-item"><label>Total per Row:</label><span>{viewingCategory.totalSeatsPerRow || 0}</span></div>
                    <div className="detail-item"><label>Layout Count:</label><span>{viewingCategory.layoutCount || 0}</span></div>
                    <div className="detail-item"><label>Available Seat Types:</label><span>{viewingCategory.availableSeatTypes && Array.isArray(viewingCategory.availableSeatTypes) ? viewingCategory.availableSeatTypes.join(', ') : 'N/A'}</span></div>
                    {viewingCategory.createdAt && (<div className="detail-item"><label>Created At:</label><span>{new Date(viewingCategory.createdAt).toLocaleDateString()}</span></div>)}
                    {viewingCategory.updatedAt && (<div className="detail-item"><label>Updated At:</label><span>{new Date(viewingCategory.updatedAt).toLocaleDateString()}</span></div>)}
                  </div>
                  {viewingCategory.configuration && (
                    <div className="config-preview-section">
                      <h5>Configuration Details:</h5>
                      <div className="seat-visualization">
                        <div className="seat-row">
                          {Array.from({ length: viewingCategory.leftSeats || 0 }).map((_, i) => (<div key={`left-${i}`} className="seat left">L{i + 1}</div>))}
                          <div className="aisle">Aisle</div>
                          {Array.from({ length: viewingCategory.rightSeats || 0 }).map((_, i) => (<div key={`right-${i}`} className="seat right">R{i + 1}</div>))}
                        </div>
                        <div className="visual-info"><p>Left Seats: {viewingCategory.leftSeats || 0}</p><p>Right Seats: {viewingCategory.rightSeats || 0}</p><p>Total per Row: {viewingCategory.totalSeatsPerRow || 0}</p></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-btn close" onClick={() => setViewingCategory(null)}>Close</button>
            </div>
          </div>
        </div>
      )}


      {viewingLayout && (
        <div className="modal-overlay" onClick={() => setViewingLayout(null)}>
          <div className="modal-content1 modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Layout Details</h3>
              <button className="modal-close-btn" onClick={() => setViewingLayout(null)}>×</button>
            </div>
            <div className="modal-body">
              {viewLoading ? (
                <div className="loading-container"><p>Loading layout details...</p></div>
              ) : (
                <div className="layout-details">
                  <div className="detail-header">
                    <h4>{safeText(viewingLayout.name)}</h4>
                    <div className="header-tags">
                      <span className={`seat-type-badge ${viewingLayout.seatType}`}>{safeText(viewingLayout.seatType)}</span>
                      <span className={`status-badge ${viewingLayout.isActive ? 'active' : 'inactive'}`}>{viewingLayout.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                  <p className="description">{safeText(viewingLayout.description)}</p>
                  <div className="layout-stats-grid">
                    <div className="stat-card-sm"><h6>Total Seats</h6><p className="stat-number">{viewingLayout.totalSeats || 0}</p></div>
                    <div className="stat-card-sm"><h6>Configuration</h6><p className="stat-number">{safeText(viewingLayout.configuration)}</p></div>
                    <div className="stat-card-sm"><h6>Rows</h6><p className="stat-number">{viewingLayout.rows || 0}</p></div>
                    <div className="stat-card-sm"><h6>Seats per Row</h6><p className="stat-number">{viewingLayout.seatsPerRow || 0}</p></div>
                    <div className="stat-card-sm"><h6>Back Seats</h6><p className="stat-number">{viewingLayout.backSeats || 0}</p></div>
                    <div className="stat-card-sm"><h6>Columns</h6><p className="stat-number">{viewingLayout.columns || 0}</p></div>
                  </div>
                  <div className="category-info"><h5>Category Information</h5><div className="category-detail"><strong>Category:</strong><span>{viewingLayout.category && typeof viewingLayout.category === 'object' ? viewingLayout.category.name || 'Unknown' : safeText(viewingLayout.category)}</span></div></div>
                  {viewingLayout.seats && Array.isArray(viewingLayout.seats) && viewingLayout.seats.length > 0 && (
                    <div className="seats-section"><h5>Seats Arrangement ({viewingLayout.seats.length} seats)</h5><div className="seats-grid">{viewingLayout.seats.slice(0, 50).map(seat => (<div key={seat.seatNumber} className="seat-item"><span className="seat-number">{seat.seatNumber}</span><span className="seat-info">Row: {seat.row}, Col: {seat.column}, Type: {seat.seatType}</span></div>))}{viewingLayout.seats.length > 50 && (<div className="more-seats">+ {viewingLayout.seats.length - 50} more seats...</div>)}</div></div>
                  )}
                  <div className="timestamps">
                    {viewingLayout.createdAt && (<div className="timestamp"><strong>Created:</strong> {new Date(viewingLayout.createdAt).toLocaleString()}</div>)}
                    {viewingLayout.updatedAt && (<div className="timestamp"><strong>Updated:</strong> {new Date(viewingLayout.updatedAt).toLocaleString()}</div>)}
                    {viewingLayout.createdBy && (<div className="timestamp"><strong>Created By:</strong>{typeof viewingLayout.createdBy === 'object' ? viewingLayout.createdBy.name || 'Admin' : safeText(viewingLayout.createdBy)}</div>)}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-btn close" onClick={() => setViewingLayout(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatManagement;